/**
 * The Lyrixa Studio workspace — the full-page replacement for the Studio drawer.
 *
 * Layout, top to bottom:
 *
 *   ☰ ← Lyrixa STUDIO ......................  (☰ on phones only; back to library)
 *   MUSIC LIBRARY  [track][track][track] →       (switch song without leaving)
 *   ─────────────────────────────────────────────
 *   ACTIVE SONG
 *   ┌────────┐  Golden Hour
 *   │ COVER  │  Lo-Fi instrumental
 *   │ ARTWORK│  4:05 · 74 BPM · Nostalgic
 *   └────────┘  ♥ Favorite  Share  Download  ⋯
 *   ─────────────────────────────────────────────
 *   AI TOOLS  │  TIMELINE / tool panel  │  LYRICS
 *   ─────────────────────────────────────────────
 *     ◀ ▶ ▶  ──waveform──  02:14 / 04:05  🔊 ⋯     ← floating, inset
 *   ‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧‧
 *
 * Why this replaces a drawer: the drawer was a 420px column that had to hold the
 * song, its player, six tools, versions and a lyric sheet, so everything took turns
 * — opening a tool hid the song, and the player scrolled away while you used it.
 * The workspace gives each of those its own permanent region, which is what makes it
 * a place to work rather than a panel to visit.
 *
 * The centre column is the one thing that swaps: the timeline by default, and a
 * tool's panel when a tool is selected. The rail, the track, the lyrics and the
 * transport never move, so you always know where you are and what is playing.
 *
 * Every control the drawer had is still here. Nothing was dropped: favourite,
 * download, delete and the downloads panel are on the ACTIVE SONG panel or in its
 * menu; the six tools and version history are in the rail; lyrics
 * copy/download/print head the lyrics column; play, volume, speed, repeat and the
 * scrubber are in the transport. The lyrics column also edits the sheet and
 * regenerates the song from it — see LyricsPanel.
 *
 * TWO RULES ABOUT THE DIVISION OF LABOUR, and they are the same rule twice:
 *
 *   Playback belongs to the transport, exclusively. The ACTIVE SONG panel has no
 *   play button, no scrubber, no waveform and no volume. Two places to control one
 *   player is two places that can disagree.
 *
 *   Saving belongs to ACTIVE SONG, exclusively. The transport carries no Download
 *   and no Export — writing a file is not a transport control. Download is a button
 *   on ACTIVE SONG, Export options is in its menu, and the Download Center is a
 *   panel in the centre column; all three still work.
 *
 * The ACTIVE SONG panel sits outside the swapping centre column deliberately, so
 * the cover and the song's identity are on screen whichever tool is open, and the
 * artwork doubles as the way into the Cover Art tool.
 *
 * SIMULATED throughout — no audio, no real generation. See TransportBar, Timeline
 * and src/hooks/useStudio.ts for what each piece actually does.
 */
import { useState, type ReactNode } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp, Menu } from 'lucide-react';
import ActiveSong from './ActiveSong';
import LibraryStrip from './LibraryStrip';
import LyricsPanel from './LyricsPanel';
import Timeline from './Timeline';
import ToolRail from './ToolRail';
import TransportBar from './TransportBar';
import { useClaimNavToggle, useNavSidebarContext } from '../layout/navSidebarContext';
import { Tooltip } from '../ui';
import { formatTime } from '../../lib/formatting';
import { useMediaQuery } from '../../hooks';
import { c } from '../../styles/theme';
import type { Song, StudioActionKind, StudioDrawer } from '../../types/domain';

export interface StudioWorkspaceProps {
  song: Song;
  /** The Studio-facing song identity — the loaded version when one has been opened. */
  displaySong?: Song;
  /** Mobile-only presentation split: lyrics stay in the workspace and tools move to the sheet. */
  mobile?: boolean;
  /** Whether the mobile tools sheet is open. */
  mobileToolsOpen?: boolean;
  /** Opens the mobile tools sheet onto its grouped list. */
  onOpenMobileTools?: () => void;
  /** Closes the mobile tools sheet. */
  onCloseMobileTools?: () => void;
  /** Opens the Timeline inside the mobile tools sheet. */
  onOpenMobileTimeline?: () => void;
  /** The section/panel currently shown inside the mobile tools sheet, or null for the list. */
  mobileToolSheetContent?: ReactNode | null;
  /** Current mobile section label shown beside the Tools trigger. */
  mobileToolCurrentLabel?: string;
  /** The songs the strip offers — the whole library, or the active project's. */
  songs: Song[];
  /**
   * What `songs` is, for the strip's eyebrow and its accessible name.
   *
   * Omitted for the library, which is the strip's own default. Passed when the visit
   * belongs to a Studio project. See LibraryStrip.
   */
  songsHeading?: string;
  /** The open tool panel, or null for the timeline. */
  drawer: StudioDrawer;
  /** The tool panel to render in the centre column when `drawer` is set. */
  panel?: ReactNode;
  onSelectTool: (panel: StudioDrawer) => void;

  playing: boolean;
  /**
   * True when this song is the loaded one, running or paused.
   *
   * Distinct from `playing` so a paused track keeps its position: the transport's clock,
   * the seek bar and the timeline playhead all key off this, and the play button's label
   * and the waveform's animation key off `playing`. Defaults to `playing` for callers
   * with no pause state of their own.
   */
  active?: boolean;
  elapsed: number;
  durationSec: number;
  /** Optional override for the total-time label when the transport is focused on preview audio. */
  totalLabel?: string;
  volume: number;
  speed: number;
  repeat: boolean;
  muted: boolean;
  onTogglePlay: () => void;
  onSetVolume: (volume: number) => void;
  onSetSpeed: (speed: number) => void;
  onToggleRepeat: () => void;
  onToggleMute: () => void;
  /** Moves the playback position. */
  onSeek: (seconds: number) => void;
  /** Opens the full-screen player. */
  onOpenFullscreen?: () => void;
  /** True while the docked transport is controlling a live Mix preview instead of the song. */
  previewMode?: boolean;
  /** True while the transport's preview-side playback is still loading. */
  previewLoading?: boolean;
  /** Short preview-state label shown on the transport when previewMode is on. */
  previewLabel?: string;
  /** Toggles the docked transport between preview focus and the head song. */
  onTogglePreviewMode?: () => void;

  onToggleFavorite: () => void;
  /** Opens the share dialog for this song. */
  onShare: () => void;
  onDownload: () => void;
  onCopyLyrics: () => void;
  onDownloadLyrics: () => void;
  onPrintLyrics: () => void;
  /**
   * Playback position as a share of the open song's length, 0–1 — used by the untimed
   * section-follow fallback when this song has no real lyric timings.
   */
  lyricPosition?: number | null;
  /** Playback position in seconds — used when this song carries real line timings. */
  lyricElapsedSec?: number | null;
  /** Persists edited lyrics. */
  onSaveLyrics: (lyrics: string) => void;
  /** Queues a regeneration from the edited lyrics, behind the credit confirmation. */
  onRegenerate: (lyrics: string) => void;
  /** True while a Studio action is running. */
  processing: boolean;
  /** Which Studio action is currently running, when known. */
  processingActionKind?: StudioActionKind | null;
  onDelete: () => void;
  /** Raises the "Add to playlist" sheet from the ACTIVE SONG menu. */
  onAddToPlaylist: () => void;
  /**
   * Raises the existing Edit Song modal from the ACTIVE SONG menu.
   *
   * Passed straight through to ActiveSong rather than handled here: the workspace routes
   * it, the route owns it, and it opens the library's own modal on the shared uiStore
   * target — see ActiveSongProps.onEditMetadata.
   */
  onEditMetadata: () => void;
  /** Returns to the Studio project list. */
  onClose: () => void;

  /** Href builder for the library strip's links. */
  hrefFor: (songId: string) => string;
  /** Selects the previous/next song. Absent at the ends of the library. */
  onPrev?: () => void;
  onNext?: () => void;
}

export default function StudioWorkspace({
  song,
  displaySong,
  mobile = false,
  mobileToolsOpen = false,
  onOpenMobileTools,
  onCloseMobileTools,
  onOpenMobileTimeline,
  mobileToolSheetContent = null,
  mobileToolCurrentLabel,
  songs,
  songsHeading,
  drawer,
  panel,
  onSelectTool,
  playing,
  active = playing,
  elapsed,
  durationSec,
  totalLabel,
  volume,
  speed,
  repeat,
  muted,
  onTogglePlay,
  onSetVolume,
  onSetSpeed,
  onToggleRepeat,
  onToggleMute,
  onSeek,
  onOpenFullscreen,
  previewMode = false,
  previewLoading = false,
  previewLabel,
  onTogglePreviewMode,
  onToggleFavorite,
  onShare,
  onDownload,
  onCopyLyrics,
  onDownloadLyrics,
  onPrintLyrics,
  lyricPosition = null,
  lyricElapsedSec = null,
  onSaveLyrics,
  onRegenerate,
  processing,
  processingActionKind = null,
  onDelete,
  onAddToPlaylist,
  onEditMetadata,
  onClose,
  hrefFor,
  onPrev,
  onNext,
}: StudioWorkspaceProps) {
  const workspaceSong = displaySong ?? song;
  /*
    The Studio is the one route that wants the whole window, so it is the one route that
    most needs to be able to put the navigation away — and on a phone, where the sidebar
    is a drawer that is shut by default, it is the only place the navigation can be
    opened from at all.

    Read from the shell's context rather than by calling useNavSidebar() again: that hook
    installs listeners, and a second copy would install them twice. Null when the
    workspace is rendered outside the shell (unit tests), in which case there is no
    sidebar to toggle and the button is simply not offered.
  */
  const nav = useNavSidebarContext();
  // The toggle below is this screen's own, in its own header, so the shell stands its
  // fallback button down while the workspace is mounted. Two hamburgers on one phone
  // screen is worse than the one problem it was solving.
  // Claimed unconditionally even though the button below is conditional, and the two
  // still agree: the shell only offers its fallback in drawer mode, which is exactly
  // when this screen does render one. Above drawer widths neither appears, because the
  // sidebar itself is on screen.
  useClaimNavToggle();

  /*
    THE HEADER'S NAV BUTTON EXISTS ONLY IN DRAWER MODE, and the reason is the difference
    between a control that is needed and one that is merely available.

    Below 900px the sidebar is off-screen entirely, so this button is the only way to
    reach the navigation from the Studio — removing it would leave the workspace with no
    route out except the browser's back button.

    Above 900px the sidebar is a column on the page and the navigation is already fully
    reachable, so there is nothing for this button to do. It used to narrow that column,
    but the sidebar's collapse control has been removed from the app entirely and this
    one is not a replacement for it. Nothing is lost: the icon rail still appears below
    900px, from the stylesheet.
  */
  const showNavToggle = nav?.drawer ?? false;
  const tabletLayout = useMediaQuery('(max-width: 1100px)') && !mobile;
  const [tabletLyricsOpen, setTabletLyricsOpen] = useState(false);

  return (
    <div className="lyx-ws">
      {/* ---- title bar ---- */}
      <div
        className={['lyx-ws-top', nav && showNavToggle ? 'lyx-ws-top-with-nav-open' : '']
          .filter(Boolean)
          .join(' ')}
        style={{ borderBottom: `1px solid ${c.border}` }}
      >
        {nav && showNavToggle && (
          <Tooltip label="Open the menu">
            <button
              type="button"
              onClick={nav.toggle}
              className="lyx-ws-back lyx-ws-nav-open"
              /*
                Named for what it does to the navigation, not for the icon: "Menu" alone
                does not say whether pressing it opens or closes, and aria-expanded is
                the pairing that makes the state readable to a screen reader.

                It stays "Open" while the drawer is open rather than becoming "Close",
                because the drawer has its own close button with that name and two
                controls answering to one name is a menu you cannot describe. This one
                is inert at that moment — see below.
              */
              aria-label="Open navigation menu"
              aria-expanded={nav.drawerOpen}
              /*
                While the drawer is open this button is underneath the scrim, so it is
                taken out of the tab order and out of the accessibility tree to match —
                otherwise a keyboard or screen-reader user could reach a control that a
                mouse user cannot, and it would be the second thing named for opening a
                menu that is already open.

                Made inert rather than unmounted so the element survives, which is what
                lets the drawer hand focus back to it on close. See Sidebar.
              */
              aria-hidden={nav.drawerOpen || undefined}
              tabIndex={nav.drawerOpen ? -1 : undefined}
            >
              {/* A hamburger, and only a hamburger: this button exists only where it
                  summons an overlay. The panel icons it used to show on a desktop went
                  with the collapse behaviour — see above. */}
              <Menu size={15} color={c.text2} />
            </button>
          </Tooltip>
        )}
        <button
          type="button"
          onClick={onClose}
          className="lyx-ws-back"
          aria-label="Back to Studio projects"
        >
          <ArrowLeft size={15} color={c.text2} />
        </button>
        <h1
          style={{
            fontFamily: 'Space Grotesk',
            fontWeight: 600,
            fontSize: 14,
            letterSpacing: '0.02em',
            margin: 0,
            color: c.text1,
            flex: 1,
            minWidth: 0,
          }}
        >
          Lyrixsa Studio
        </h1>
      </div>

      <LibraryStrip songs={songs} currentId={song.id} hrefFor={hrefFor} heading={songsHeading} />

      {/* ---- the track you are working on. No playback controls here. ---- */}
      <ActiveSong
        song={workspaceSong}
        onToggleFavorite={onToggleFavorite}
        onShare={onShare}
        onDownload={onDownload}
        onDelete={onDelete}
        onAddToPlaylist={onAddToPlaylist}
        onEditMetadata={onEditMetadata}
        onSelectTool={onSelectTool}
        drawer={drawer}
      />

      {/* ---- the three columns ---- */}
      <div className="lyx-ws-body">
        <ToolRail
          song={song}
          active={drawer}
          onSelect={onSelectTool}
          mobile={mobile}
          mobileSheetOpen={mobileToolsOpen}
          onOpenMobileSheet={onOpenMobileTools}
          onCloseMobileSheet={onCloseMobileTools}
          onOpenTimeline={onOpenMobileTimeline}
          mobileSheetContent={mobileToolSheetContent}
          mobileCurrentLabel={mobileToolCurrentLabel}
        />

        {/* The centre swaps between the timeline and whichever tool is open. It is
            the only region that changes, which is what keeps the workspace stable
            underneath you. On mobile, the tools sheet owns this content instead and the
            persistent workspace keeps only lyrics underneath the Tools trigger. */}
        {!mobile && (
          <div
            className={[
              'lyx-ws-centre',
              tabletLayout && tabletLyricsOpen ? 'lyx-ws-tablet-panel-hidden' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {panel ?? (
              // `speed` reaches the timeline for the same reason it reaches the transport:
              // the playhead is smoothed over one tick, and the tick is shorter at 2×.
              <Timeline
                song={workspaceSong}
                playing={playing}
                active={active}
                elapsed={elapsed}
                speed={speed}
              />
            )}
          </div>
        )}

        {/* Keyed on the song id so switching tracks in the library strip discards
            the previous song's unsaved draft rather than carrying it across — the
            alternative is an effect that also wipes a draft whenever anything else
            about the song is patched. See LyricsPanel's header. */}
        <LyricsPanel
          key={workspaceSong.id}
          className={
            tabletLayout
              ? tabletLyricsOpen
                ? 'lyx-ws-tablet-panel-visible'
                : 'lyx-ws-tablet-panel-hidden'
              : undefined
          }
          song={workspaceSong}
          onCopy={onCopyLyrics}
          onDownload={onDownloadLyrics}
          onPrint={onPrintLyrics}
          at={lyricPosition}
          elapsedSec={lyricElapsedSec}
          onSave={onSaveLyrics}
          onRegenerate={onRegenerate}
          processing={processing}
          processingActionKind={processingActionKind}
        />

        {tabletLayout && (
          <button
            type="button"
            className="lyx-ws-tablet-lyrics-toggle"
            aria-expanded={tabletLyricsOpen}
            onClick={() => setTabletLyricsOpen((open) => !open)}
          >
            <span>{tabletLyricsOpen ? 'Collapse lyrics' : 'Lyrics'}</span>
            {tabletLyricsOpen ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
          </button>
        )}
      </div>

      <TransportBar
        song={workspaceSong}
        totalLabel={totalLabel ?? (active && durationSec > 0 ? formatTime(durationSec) : workspaceSong.length)}
        playing={playing}
        active={active}
        elapsed={elapsed}
        // The version-aware runtime, so the scrubber spans a loaded version's length —
        // the same value that already drives the total label above.
        durationSec={durationSec}
        volume={volume}
        speed={speed}
        repeat={repeat}
        muted={muted}
        previewMode={previewMode}
        previewLoading={previewLoading}
        previewLabel={previewLabel}
        onTogglePreviewMode={onTogglePreviewMode}
        onTogglePlay={onTogglePlay}
        onSetVolume={onSetVolume}
        onSetSpeed={onSetSpeed}
        onToggleRepeat={onToggleRepeat}
        onToggleMute={onToggleMute}
        onSeek={onSeek}
        onOpenFullscreen={onOpenFullscreen}
        onPrev={onPrev}
        onNext={onNext}
      />
    </div>
  );
}
